"use client";

/**
 * The sheet (composer panels, spec 2026-09-19 §2): a ts-need paper holding
 * OutputDocCard. The headline is the shell's own title line, so the doc body
 * renders without it; the source is the quiet why, and its Open goes to the
 * task, the creation or the file the server named (routeFor, never a URL the
 * model wrote).
 */
import {
  routeFor,
  type ComposerPanel,
  type PanelSource,
  type PanelSourceStatus,
} from "@bash/shared";
import { AskShell } from "@/components/task-story/AskShell";
import { OutputDocCard } from "@/components/home-briefing/OutputDocCard";
import { STATUS_WORD } from "@/lib/task-live-status";

type Sheet = Extract<ComposerPanel, { kind: "sheet" }>;

/** The source's seven words ARE the board's: one table, and the key type pins
 *  the wire's vocabulary to the web's (a status with no word fails here). */
const SOURCE_WORD: Record<PanelSourceStatus, string> = STATUS_WORD;

export function sourceRoute(source: PanelSource): string {
  return routeFor(source.kind, source.id);
}

export function sourceLine(source: PanelSource): string {
  if (source.kind === "task")
    return `From the task: ${source.label} · ${SOURCE_WORD[source.status]}`;
  return source.kind === "creation"
    ? `A creation: ${source.label}`
    : `A file: ${source.label}`;
}

function SheetGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#fff"
        d="M7 2.2h7.2l5.4 5.4V19a2.8 2.8 0 0 1-2.8 2.8H7A2.8 2.8 0 0 1 4.2 19V5A2.8 2.8 0 0 1 7 2.2z"
      />
      <path className="g-fill" opacity=".3" d="M14.2 2.2v5.4h5.4z" />
      <path
        className="g-ink"
        fill="none"
        strokeWidth="2.2"
        strokeLinecap="round"
        d="M8 12h8M8 15.5h8M8 19h5"
      />
    </svg>
  );
}

export function SheetPanel({
  panel,
  onClose,
  onOpen,
}: {
  panel: Sheet;
  onClose: () => void;
  onOpen: (route: string) => void;
}) {
  const source = panel.source;
  return (
    <AskShell
      kind="sheet"
      tile={<SheetGlyph />}
      title={panel.doc.headline}
      why={source ? sourceLine(source) : undefined}
      onClose={onClose}
    >
      <div className="mt-2.5" data-testid="panel-sheet-doc">
        <OutputDocCard doc={panel.doc} headline={false} scroll />
      </div>
      {source && (
        <div className="ts-need-acts">
          <button
            type="button"
            className="ts-need-btn"
            onClick={() => onOpen(sourceRoute(source))}
          >
            Open
          </button>
        </div>
      )}
    </AskShell>
  );
}
